import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useCreateGstRate,
  useDeleteGstRate,
  useGstRateSummary,
  useGstRatesPage,
  useSetDefaultGstRate,
  useUpdateGstRate,
} from '../../hooks/useGstRates';
import { GstRate, GstRateInput } from '../../services/gst-rate.service';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const formatRate = (rate: number) => `${formatNumber(Number(rate), 2)}%`;
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const SORTS = [
  { value: 'rate-asc', label: 'Lowest rate first', sortBy: 'rate', sortOrder: 'ASC' },
  { value: 'rate-desc', label: 'Highest rate first', sortBy: 'rate', sortOrder: 'DESC' },
  { value: 'name', label: 'Name A–Z', sortBy: 'name', sortOrder: 'ASC' },
  { value: 'updated', label: 'Recently changed', sortBy: 'updatedAt', sortOrder: 'DESC' },
] as const;

interface GstRateFormProps {
  gstRate: GstRate | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: GstRateInput) => void;
}

const GstRateForm: React.FC<GstRateFormProps> = ({ gstRate, isPending, onCancel, onSubmit }) => {
  const isEdit = !!gstRate;
  // HSN codes store the percentage itself, so a rate that is in use keeps its percentage
  const rateLocked = isEdit && (gstRate?.hsnCodes ?? 0) > 0;

  const [form, setForm] = useState({
    rate: gstRate ? String(gstRate.rate) : '',
    name: gstRate?.name ?? '',
    description: gstRate?.description ?? '',
    isActive: gstRate ? gstRate.isActive : true,
    isDefault: gstRate ? gstRate.isDefault : false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const rate = Number(form.rate);
  const rateValid = form.rate.trim() !== '' && Number.isFinite(rate) && rate >= 0 && rate <= 100;
  const autoName = rateValid ? `GST ${formatRate(rate)}` : 'GST rate';

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!rateValid) next.rate = 'Enter a rate from 0 to 100';
    else if (round2(rate) !== rate) next.rate = 'Use at most two decimals';
    if (form.name.trim().length > 60) next.name = 'Keep the name within 60 characters';
    if (form.description.trim().length > 250) next.description = 'Keep the description within 250 characters';
    if (form.isDefault && !form.isActive) next.isActive = 'The default rate has to be active';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      rate,
      name: form.name.trim() || autoName,
      description: form.description.trim() || null,
      isActive: form.isActive,
      isDefault: form.isDefault,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="GST Rate (%)"
          htmlFor="gst-rate"
          required
          error={errors.rate}
          hint={rateLocked ? `Used by ${gstRate?.hsnCodes} HSN code(s), so the percentage is fixed` : 'Total GST, for example 18'}
        >
          <input
            id="gst-rate"
            type="number"
            min="0"
            max="100"
            step="0.01"
            autoFocus={!isEdit}
            value={form.rate}
            disabled={rateLocked}
            onChange={(event) => setForm((prev) => ({ ...prev, rate: event.target.value }))}
            className={cn(inputClass, errors.rate && inputErrorClass)}
            placeholder="18"
          />
        </Field>
        <Field label="Name" htmlFor="gst-name" error={errors.name} hint={`Shown in lists. "${autoName}" when left empty`}>
          <input
            id="gst-name"
            type="text"
            maxLength={60}
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className={cn(inputClass, errors.name && inputErrorClass)}
            placeholder={autoName}
          />
        </Field>
        <Field label="Description" htmlFor="gst-description" error={errors.description} className="sm:col-span-2">
          <input
            id="gst-description"
            type="text"
            maxLength={250}
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            className={cn(inputClass, errors.description && inputErrorClass)}
            placeholder="What this rate applies to"
          />
        </Field>
      </div>

      {/* How the rate is split on a sale */}
      <div className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">How it is charged</p>
        <dl className="mt-2 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">Sale within the state</dt>
            <dd className="font-medium text-gray-900 tabular-nums">
              {rateValid ? `CGST ${formatRate(round2(rate / 2))} + SGST ${formatRate(round2(rate / 2))}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Sale to another state</dt>
            <dd className="font-medium text-gray-900 tabular-nums">{rateValid ? `IGST ${formatRate(rate)}` : '—'}</dd>
          </div>
        </dl>
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            disabled={gstRate?.isDefault}
            onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Active</span>
            <span className="block text-xs text-gray-500">Only active rates can be picked on HSN codes, orders and invoices.</span>
          </span>
        </label>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={form.isDefault}
            disabled={gstRate?.isDefault}
            onChange={(event) => setForm((prev) => ({ ...prev, isDefault: event.target.checked }))}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Default rate</span>
            <span className="block text-xs text-gray-500">
              {gstRate?.isDefault
                ? 'This is the default. To change it, make another rate the default.'
                : 'New lines start with this rate. It replaces the current default.'}
            </span>
          </span>
        </label>
        {errors.isActive && <p className="text-sm text-red-600">{errors.isActive}</p>}
      </div>

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add GST Rate'}
        </button>
      </FormActions>
    </form>
  );
};

const GstRateManagement: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState<(typeof SORTS)[number]['value']>('rate-asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GstRate | null>(null);
  const [deleting, setDeleting] = useState<GstRate | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const activeSort = SORTS.find((option) => option.value === sort) ?? SORTS[0];
  const { data, isLoading, isError, error, isFetching, refetch } = useGstRatesPage({
    page,
    limit,
    search: search || undefined,
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    sortBy: activeSort.sortBy,
    sortOrder: activeSort.sortOrder,
  });
  const { data: summary } = useGstRateSummary();

  const createGstRate = useCreateGstRate();
  const updateGstRate = useUpdateGstRate();
  const setDefaultGstRate = useSetDefaultGstRate();
  const deleteGstRate = useDeleteGstRate();

  const gstRates = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Step back when the last page no longer exists
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter;
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (gstRate: GstRate) => {
    setEditing(gstRate);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (createGstRate.isPending || updateGstRate.isPending) return;
    setFormOpen(false);
  };

  const handleSubmit = (input: GstRateInput) => {
    const onError = (submitError: unknown) => toast.error(getApiErrorMessage(submitError, 'The GST rate could not be saved'));
    if (editing) {
      // The percentage of a rate that HSN codes use is fixed, so it is not sent again
      const { rate, ...rest } = input;
      const changes = rate === editing.rate ? rest : input;
      updateGstRate.mutate(
        { id: editing.id, data: changes },
        {
          onSuccess: (saved) => {
            toast.success(`${saved.name} updated`);
            setFormOpen(false);
          },
          onError,
        },
      );
    } else {
      createGstRate.mutate(input, {
        onSuccess: (saved) => {
          toast.success(`${saved.name} added`);
          setFormOpen(false);
        },
        onError,
      });
    }
  };

  const handleSetDefault = (gstRate: GstRate) => {
    setDefaultGstRate.mutate(gstRate.id, {
      onSuccess: () => toast.success(`${formatRate(gstRate.rate)} is now the default rate`),
      onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The default rate could not be changed')),
    });
  };

  const handleToggleActive = (gstRate: GstRate) => {
    updateGstRate.mutate(
      { id: gstRate.id, data: { isActive: !gstRate.isActive } },
      {
        onSuccess: () => toast.success(`${gstRate.name} marked ${gstRate.isActive ? 'inactive' : 'active'}`),
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The status could not be changed')),
      },
    );
  };

  const handleDelete = () => {
    if (!deleting) return;
    deleteGstRate.mutate(deleting.id, {
      onSuccess: () => {
        toast.success(`${deleting.name} deleted`);
        setDeleting(null);
      },
      onError: (actionError) => {
        toast.error(getApiErrorMessage(actionError, 'The GST rate could not be deleted'));
        setDeleting(null);
      },
    });
  };

  const stats = [
    { label: 'GST rates', value: summary ? formatNumber(summary.total) : '—', hint: 'In the master' },
    { label: 'Active', value: summary ? formatNumber(summary.active) : '—', hint: 'Offered on forms' },
    { label: 'Inactive', value: summary ? formatNumber(summary.inactive) : '—', hint: 'Kept, not offered' },
    {
      label: 'Default rate',
      value: summary ? (summary.defaultRate ? formatRate(summary.defaultRate.rate) : 'Not set') : '—',
      hint: 'New lines start with it',
    },
  ];

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to="/master-data" className="hover:text-indigo-600">
              Master Data
            </Link>
            <ChevronRightIcon className="mx-1 h-3.5 w-3.5" />
            <span className="text-gray-700">GST Rates</span>
          </nav>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">GST Rates</h1>
          <p className="mt-1 text-sm text-gray-500">
            The GST rates used across the system. HSN codes, quotes, sales orders, purchase orders and invoices offer the active ones.
          </p>
        </div>
        <button type="button" onClick={openAdd} className={primaryButtonClass}>
          <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Add GST Rate
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by rate, name or description"
              aria-label="Search GST rates"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            aria-label="Status"
            className={cn(inputClass, 'lg:w-44')}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={sort}
            onChange={(event) => {
              setSort(event.target.value as typeof sort);
              setPage(1);
            }}
            aria-label="Sort"
            className={cn(inputClass, 'lg:w-52')}
          >
            {SORTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The GST rates could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Rate</th>
                  <th scope="col" className={thClass}>Name</th>
                  <th scope="col" className={thClass}>Within the state</th>
                  <th scope="col" className={thClass}>To another state</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>HSN codes</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 7 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : gstRates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No GST rate matches' : 'No GST rates yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters ? 'Try a different search or status.' : 'Add the rates your products and services are taxed at.'}
                      </p>
                      {!hasFilters && (
                        <button type="button" onClick={openAdd} className={cn(primaryButtonClass, 'mt-4')}>
                          Add GST Rate
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  gstRates.map((gstRate) => (
                    <tr key={gstRate.id} className={cn('hover:bg-gray-50', !gstRate.isActive && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={cn('text-lg font-semibold tabular-nums', gstRate.isActive ? 'text-gray-900' : 'text-gray-400')}>
                          {formatRate(gstRate.rate)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{gstRate.name}</span>
                          {gstRate.isDefault && (
                            <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-indigo-600/20')}>Default</span>
                          )}
                        </div>
                        {gstRate.description && <p className="mt-0.5 text-sm text-gray-500">{gstRate.description}</p>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">
                        CGST {formatRate(gstRate.cgstRate)} + SGST {formatRate(gstRate.sgstRate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">IGST {formatRate(gstRate.igstRate)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                        {gstRate.hsnCodes ? formatNumber(gstRate.hsnCodes) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={cn(
                            badgeClass,
                            gstRate.isActive ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-gray-500/20',
                          )}
                        >
                          {gstRate.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!gstRate.isDefault && gstRate.isActive && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(gstRate)}
                              disabled={setDefaultGstRate.isPending}
                              title="Make default"
                              aria-label={`Make ${gstRate.name} the default`}
                              className="rounded-md p-1.5 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-50"
                            >
                              <StarIcon className="h-4 w-4" />
                            </button>
                          )}
                          {!gstRate.isDefault && (
                            <button
                              type="button"
                              onClick={() => handleToggleActive(gstRate)}
                              disabled={updateGstRate.isPending}
                              className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                              {gstRate.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(gstRate)}
                            title="Edit"
                            aria-label={`Edit ${gstRate.name}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(gstRate)}
                            disabled={gstRate.isDefault}
                            title={gstRate.isDefault ? 'The default rate cannot be deleted' : 'Delete'}
                            aria-label={`Delete ${gstRate.name}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${editing.name}` : 'Add GST Rate'}
        description={editing ? 'Orders and invoices already made keep the rate they were made with.' : 'A new rate can be picked on forms as soon as it is saved.'}
        size="lg"
      >
        {formOpen && (
          <GstRateForm
            key={editing?.id ?? 'new'}
            gstRate={editing}
            isPending={createGstRate.isPending || updateGstRate.isPending}
            onCancel={closeForm}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete GST rate"
        message={
          deleting ? (
            <>
              Delete <span className="font-semibold">{deleting.name}</span> ({formatRate(deleting.rate)})? It will no longer be offered on
              forms. Orders and invoices already made at this rate are not changed.
            </>
          ) : null
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={deleteGstRate.isPending}
      />
    </div>
  );
};

export default GstRateManagement;
